"use client";

import Link from "next/link";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { featuredServices } from "@/lib/data/site";
import { SectionHeader } from "@/components/ui/section-header";
import { useCursor } from "@/providers/cursor-provider";
import { GlowOrb } from "@/components/ui/glow-orb";
import { cn } from "@/lib/utils";

export function ServiceExplorer() {
  const [active, setActive] = useState(0);
  const current = featuredServices[active];
  const { setLabel } = useCursor();

  return (
    <section id="capabilities" className="relative overflow-hidden py-20 sm:py-24 md:py-32">
      <GlowOrb className="right-[-100px] top-20" color="accent" size={360} />
      <div className="container-wide relative">
        <SectionHeader
          eyebrow="Core Capabilities"
          title={"SOFTWARE, AI & CLOUD\nENGINEERING CAPABILITIES."}
          description="Hover a capability to explore how MaxRidge delivers custom software, generative AI, data, cloud, and consulting as one connected system."
        />

        {/* Desktop / large tablet: sidebar + preview */}
        <div className="mt-12 hidden lg:grid lg:grid-cols-[0.95fr_1.25fr] lg:gap-6 xl:gap-8">
          <div
            className="flex flex-col gap-2"
            onMouseLeave={() => setLabel(null)}
          >
            {featuredServices.map((service, index) => {
              const isActive = active === index;
              return (
                <button
                  key={service.id}
                  type="button"
                  onMouseEnter={() => {
                    setActive(index);
                    setLabel("VIEW");
                  }}
                  onFocus={() => setActive(index)}
                  onClick={() => setActive(index)}
                  className={cn(
                    "group relative w-full rounded-2xl border px-4 py-4 text-left transition duration-300 xl:px-5 xl:py-4.5",
                    isActive
                      ? "border-accent/45 bg-card shadow-[var(--shadow-glow)]"
                      : "border-transparent bg-transparent hover:border-border hover:bg-card/60",
                  )}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={cn(
                        "mt-0.5 font-mono text-[11px] tracking-wide transition",
                        isActive ? "text-accent" : "text-muted group-hover:text-accent/80",
                      )}
                    >
                      {service.number}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3
                        className={cn(
                          "font-display text-lg tracking-tight transition xl:text-xl",
                          isActive ? "text-foreground" : "text-foreground/80 group-hover:text-foreground",
                        )}
                      >
                        {service.title}
                      </h3>
                      <p
                        className={cn(
                          "mt-1 line-clamp-2 text-sm leading-relaxed transition",
                          isActive ? "text-muted" : "text-muted/70 group-hover:text-muted",
                        )}
                      >
                        {service.short}
                      </p>
                    </div>
                    <ArrowUpRight
                      className={cn(
                        "mt-1 h-4 w-4 shrink-0 transition",
                        isActive ? "translate-x-0.5 -translate-y-0.5 text-accent opacity-100" : "text-muted opacity-0 group-hover:opacity-60",
                      )}
                    />
                  </div>
                  {isActive ? (
                    <motion.span
                      layoutId="service-rail"
                      className="absolute inset-y-3 left-0 w-0.5 rounded-full bg-gradient-to-b from-accent to-cyan"
                    />
                  ) : null}
                </button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 14, filter: "blur(8px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -10, filter: "blur(6px)" }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className={cn(
                "relative min-h-[420px] overflow-hidden rounded-[1.75rem] border border-border bg-gradient-to-br xl:min-h-[460px] xl:rounded-[2rem]",
                current.accent,
              )}
            >
              <div className="absolute inset-0 bg-background/78 dark:bg-background/60 lumen:bg-[#030712]/78" />
              <div className="absolute inset-0 grid-overlay opacity-50" />
              <div className="absolute -right-16 top-10 h-48 w-48 rounded-full bg-white/10 blur-3xl" />
              <div className="absolute -bottom-20 left-10 h-40 w-40 rounded-full bg-accent/20 blur-3xl" />

              <div className="relative flex h-full flex-col p-8 xl:p-10">
                <div className="flex items-center justify-between gap-4">
                  <p className="font-mono text-xs tracking-[0.16em] text-accent">
                    CAPABILITY {current.number}
                  </p>
                  <span className="rounded-full border border-border/80 bg-card/50 px-3 py-1 text-[11px] uppercase tracking-[0.14em] text-muted backdrop-blur">
                    Hover to explore
                  </span>
                </div>

                <h3 className="mt-5 max-w-xl font-display text-3xl tracking-tight text-foreground xl:text-5xl">
                  {current.title}
                </h3>
                <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-muted xl:text-base">
                  {current.description}
                </p>

                <div className="mt-8">
                  <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">
                    What we deliver
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {current.capabilities.slice(0, 8).map((cap, i) => (
                      <motion.span
                        key={cap}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.03 * i }}
                        className="rounded-full border border-border bg-card/75 px-3.5 py-1.5 text-xs text-foreground/85 backdrop-blur lumen:border-white/15 lumen:bg-white/[0.05]"
                      >
                        {cap}
                      </motion.span>
                    ))}
                  </div>
                </div>

                <div className="mt-8 grid gap-3 sm:grid-cols-2">
                  {current.outcomes.slice(0, 4).map((outcome, i) => (
                    <motion.div
                      key={outcome}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 + i * 0.04 }}
                      className="rounded-2xl border border-border/80 bg-card/55 px-4 py-3 backdrop-blur"
                    >
                      <p className="text-sm leading-relaxed text-foreground/85">{outcome}</p>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-auto flex flex-wrap items-center gap-3 pt-10">
                  <Link
                    href={current.href}
                    className="inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 text-sm font-semibold text-white transition hover:brightness-110"
                    onMouseEnter={() => setLabel("OPEN")}
                    onMouseLeave={() => setLabel("VIEW")}
                  >
                    Explore {current.title.split("&")[0].trim()}
                    <ArrowUpRight className="h-4 w-4" />
                  </Link>
                  <Link
                    href="/contact"
                    className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 px-5 py-2.5 text-sm font-medium text-foreground/90 backdrop-blur transition hover:border-accent/40"
                  >
                    Talk to us
                  </Link>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Mobile / tablet: stacked cards */}
        <div className="mt-10 space-y-3 lg:hidden">
          {featuredServices.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.04 }}
            >
              <Link
                href={service.href}
                className="group block overflow-hidden rounded-[1.35rem] border border-border bg-card/80 transition hover:border-accent/40 sm:rounded-[1.5rem]"
              >
                <div className={cn("h-1.5 w-full bg-gradient-to-r", service.accent)} />
                <div className="p-5 sm:p-6">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-mono text-xs text-accent">{service.number}</p>
                    <ArrowUpRight className="h-4 w-4 text-muted transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-accent" />
                  </div>
                  <h3 className="mt-2 font-display text-xl tracking-tight sm:text-2xl">{service.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-muted">{service.short}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {service.capabilities.slice(0, 4).map((cap) => (
                      <span
                        key={cap}
                        className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted"
                      >
                        {cap}
                      </span>
                    ))}
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
